import { useEffect, useRef, useState } from 'react';
import './styles/switch.css';
import { playSound } from '../utils/soundEffectHelper';

type SwitchProps = {
  options: string[];
  selected: string;
  onChange: (value: string) => void;
};

function Switch(props: SwitchProps) {
  const { options, selected, onChange } = props;

  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [hasMeasured, setHasMeasured] = useState(false);

  useEffect(() => {
    const selectedIndex = options.findIndex((opt) => opt === selected);
    const selectedBtn = buttonRefs.current[selectedIndex];

    if (!selectedBtn) return;

    const updateIndicator = () => {
      setIndicatorStyle({
        left: selectedBtn.offsetLeft,
        width: selectedBtn.offsetWidth,
      });
    };

    updateIndicator();
    setHasMeasured(true);

    const resizeObserver = new ResizeObserver(updateIndicator);
    resizeObserver.observe(selectedBtn);

    window.addEventListener('resize', updateIndicator);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateIndicator);
    };
  }, [selected, options]);

  return (
    <div className="switch-container">
      {/* Sliding indicator (only shown after initial measure) */}
      {hasMeasured && (
        <div
          className="switch-indicator"
          style={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
          }}
        />
      )}

      {options.map((option, index) => (
        <button
          key={option}
          ref={(el) => {
            buttonRefs.current[index] = el;
          }}
          onClick={() => {
            if (selected !== option) playSound('switchClick');
            onChange(option);
          }}
          className={`switch-button ${
            selected === option ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

export default Switch;
